import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Award,
  BadgeCheck,
  ExternalLink,
  FileText,
  Loader2,
  Palette,
  Plus,
  RefreshCcw,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import Header from "../components/layout/Header";
import IssueCertificateModal from "../components/modals/IssueCertificateModal";
import RevokeCertificateModal from "../components/modals/RevokeCertificateModal";
import CertificateBranding from "../components/CertificateBranding";
import { certificateService } from "../services/certificateService";
import { studentService } from "../services/studentService";

/**
 * Certificates page — school-admin scoped.
 *
 * Layout matches ManageStudents: page title + primary CTA on top, stat
 * cards underneath, then a table of issued certs. Two modals piggy-back:
 *   - IssueCertificateModal — issuance form
 *   - RevokeCertificateModal — confirmation with optional reason
 *
 * The list polls automatically for ~30s after an issuance (the PDF render
 * happens async via pg-boss; we want the pdfUrl to surface ASAP without
 * making the user hit refresh).
 */
const PDF_POLL_INTERVAL_MS = 3000;
const PDF_POLL_MAX_MS = 30000;

/** Map CertificateType → friendly chip label. */
const TYPE_LABEL = {
  GRADUATION: "Graduation",
  HONOUR_ROLL: "Honour Roll",
  AWARD: "Award",
  PARTICIPATION: "Participation",
  COMPLETION: "Completion",
  CUSTOM: "Custom",
};

export default function Certificates() {
  const { t } = useTranslation();

  // "issued" = stats + issuance history; "branding" = certificate branding
  // settings (moved here from the Settings page).
  const [tab, setTab] = useState("issued");

  const [certs, setCerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);

  const [schoolName, setSchoolName] = useState("");
  const [adminName, setAdminName] = useState("");

  const [showIssueModal, setShowIssueModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  // After issuance, we poll until pdfUrl populates or we hit max wait.
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await certificateService.listCertificates({ page, pageSize });
      setCerts(result.items || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error("Failed to load certificates:", err);
      toast.error(err.message || "Couldn't load certificates");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  // Pull the current admin's school/name for the Header. The students
  // endpoint conveniently bundles this in its response shape.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await studentService.getStudents({ limit: 1, page: 1 });
        if (cancelled) return;
        if (result?.schoolName) setSchoolName(result.schoolName);
        if (result?.adminName) setAdminName(result.adminName);
      } catch {
        // Non-fatal — Header just falls back to empty labels.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Polling loop — reload the list every 3s for up to 30s after an
  // issuance. Stops automatically once every recent cert has a pdfUrl.
  useEffect(() => {
    if (!polling) return undefined;
    const start = Date.now();
    const interval = setInterval(async () => {
      await load();
      const elapsed = Date.now() - start;
      const allRendered = certs.length === 0 || certs.every((c) => c.pdfUrl);
      if (elapsed > PDF_POLL_MAX_MS || allRendered) {
        setPolling(false);
      }
    }, PDF_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // Intentionally don't depend on `certs` — we want the latest snapshot
    // each tick via the closure over `load`. Adding it would reset the
    // interval on every list refresh and never let polling stop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, load]);

  const stats = useMemo(() => {
    const active = certs.filter((c) => c.status === "ISSUED").length;
    const revoked = certs.filter((c) => c.status === "REVOKED").length;
    return { total, active, revoked };
  }, [certs, total]);

  // Issue handler used by the modal for both single and cohort modes.
  // The modal calls this once per recipient in cohort mode (chunked
  // Promise.allSettled), passing meta.batch=true on each call. We
  // suppress per-cert toasts in batch mode and emit one summary toast +
  // close at the end (meta.batchLast tells us when).
  const handleIssue = async (payload, meta = {}) => {
    const result = await certificateService.issueCertificate(payload);
    if (!meta.batch) {
      // Single issue — toast immediately, close the modal, refresh, poll.
      toast.success(
        t("certificates.toast.issued", {
          defaultValue: "Certificate issued. Render in progress…",
        }),
      );
      setShowIssueModal(false);
      await load();
      setPolling(true);
    }
    return result;
  };

  // The modal calls this once it's done with a cohort batch (success or
  // partial). Keeps the modal open on partial failure so the user sees
  // the failure detail; closes on full success.
  const handleBatchFinished = async ({ total, failures }) => {
    if (failures.length === 0) {
      toast.success(
        t("certificates.toast.issuedBatchAll", {
          defaultValue: "Issued {{count}} certificates. Renders in progress…",
          count: total,
        }),
      );
      setShowIssueModal(false);
    } else {
      toast.warn(
        t("certificates.toast.issuedBatchPartial", {
          defaultValue: "Issued {{ok}} of {{total}}. {{fail}} failed.",
          ok: total - failures.length,
          total,
          fail: failures.length,
        }),
      );
      // Modal stays open so the admin can review failures inline.
    }
    await load();
    setPolling(true);
  };

  const handleRevokeConfirm = async (reason) => {
    if (!revokeTarget) return;
    try {
      await certificateService.revokeCertificate(revokeTarget.id, reason);
      toast.success(t("certificates.toast.revoked", { defaultValue: "Certificate revoked." }));
      setRevokeTarget(null);
      await load();
    } catch (err) {
      throw err;
    }
  };

  const handleRequeue = async (cert) => {
    try {
      await certificateService.requeueRender(cert.id);
      toast.success(
        t("certificates.toast.requeued", { defaultValue: "Render re-enqueued. Refreshing…" }),
      );
      setPolling(true);
    } catch (err) {
      toast.error(err.message || "Couldn't requeue render");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="bg-white min-h-screen font-manrope pb-[100px]">
      <Header schoolName={schoolName} adminName={adminName} />

      <div className="ml-[23px] mt-6 px-8 flex flex-col gap-6">
        {/* Page title + primary CTA */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-gray-900 font-bold text-2xl">
              {t("certificates.title", { defaultValue: "Certificates" })}
            </h2>
            <p className="text-gray-500 text-base">
              {t("certificates.subtitle", {
                defaultValue: "Issue and manage school-issued certificates",
              })}
            </p>
          </div>
          {/* Issue CTA only on the Issued tab; hidden in the empty state — the
              EmptyState below has its own CTA (redundant double button).
              Gated on !loading rather than (loading || certs.length > 0):
              while the fetch is in flight we don't yet know whether the empty
              state is about to render, so showing the button optimistically
              meant a school with no certificates watched it appear and then
              get yanked away a second later — which reads as a glitch. Now it
              renders once the answer is known: header button when there are
              certificates, EmptyState CTA when there aren't. */}
          {tab === "issued" && !loading && certs.length > 0 ? (
            <button
              type="button"
              onClick={() => setShowIssueModal(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] bg-primary text-white text-sm font-semibold shadow-sm hover:bg-blue-600 transition"
            >
              <Plus size={16} />
              {t("certificates.issue", { defaultValue: "Issue Certificate" })}
            </button>
          ) : null}
        </div>

        {/* Tabs: issued list vs. certificate branding settings */}
        <div className="flex items-center gap-6 border-b border-gray-200 -mt-2">
          <button
            type="button"
            onClick={() => setTab("issued")}
            className={`inline-flex items-center gap-1.5 pb-3 -mb-px border-b-2 text-sm font-semibold transition-colors ${
              tab === "issued" ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <FileText size={15} />
            {t("certificates.tabs.issued", { defaultValue: "Issued" })}
          </button>
          <button
            type="button"
            onClick={() => setTab("branding")}
            className={`inline-flex items-center gap-1.5 pb-3 -mb-px border-b-2 text-sm font-semibold transition-colors ${
              tab === "branding" ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <Palette size={15} />
            {t("certificates.tabs.branding", { defaultValue: "Branding" })}
          </button>
        </div>

        {tab === "issued" && (
          <>
        {/* Stat cards */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            icon={Award}
            iconColor="text-blue-600"
            bg="bg-blue-50"
            label={t("certificates.stats.total", { defaultValue: "Total issued" })}
            value={stats.total}
          />
          <StatCard
            icon={BadgeCheck}
            iconColor="text-emerald-600"
            bg="bg-emerald-50"
            label={t("certificates.stats.active", { defaultValue: "Active" })}
            value={stats.active}
          />
          <StatCard
            icon={ShieldAlert}
            iconColor="text-red-600"
            bg="bg-red-50"
            label={t("certificates.stats.revoked", { defaultValue: "Revoked" })}
            value={stats.revoked}
          />
        </div>

        {/* History */}
        <div className="rounded-[16px] border border-gray-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-gray-900">
                {t("certificates.history", { defaultValue: "Issuance history" })}
              </h3>
              <p className="text-xs text-gray-500">
                {t("certificates.historySubtitle", {
                  defaultValue: "Newest first. Revoked certificates remain queryable on the public verify page.",
                })}
              </p>
            </div>
            {polling && (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                <Loader2 size={12} className="animate-spin" />
                {t("certificates.polling", { defaultValue: "Waiting for PDF render…" })}
              </span>
            )}
          </div>

          {loading ? (
            <div className="p-10 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              {t("certificates.loading", { defaultValue: "Loading…" })}
            </div>
          ) : certs.length === 0 ? (
            <EmptyState onIssue={() => setShowIssueModal(true)} />
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-5 py-3">{t("certificates.cols.student", { defaultValue: "Student" })}</th>
                  <th className="px-5 py-3">{t("certificates.cols.type", { defaultValue: "Type" })}</th>
                  <th className="px-5 py-3">{t("certificates.cols.title", { defaultValue: "Title" })}</th>
                  <th className="px-5 py-3">{t("certificates.cols.issued", { defaultValue: "Issued" })}</th>
                  <th className="px-5 py-3">{t("certificates.cols.status", { defaultValue: "Status" })}</th>
                  <th className="px-5 py-3 text-right">{t("certificates.cols.actions", { defaultValue: "Actions" })}</th>
                </tr>
              </thead>
              <tbody>
                {certs.map((cert) => (
                  <CertRow
                    key={cert.id}
                    cert={cert}
                    onRevoke={() => setRevokeTarget(cert)}
                    onRequeue={() => handleRequeue(cert)}
                  />
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination — minimal, parity with manageStudents footer style. */}
          {!loading && certs.length > 0 && totalPages > 1 && (
            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {t("certificates.showing", {
                  defaultValue: "Showing {{from}} to {{to}} of {{total}}",
                  from: (page - 1) * pageSize + 1,
                  to: Math.min(page * pageSize, total),
                  total,
                })}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-[8px] border border-gray-300 text-gray-700 disabled:opacity-40"
                >
                  {t("certificates.prev", { defaultValue: "Previous" })}
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 rounded-[8px] border border-gray-300 text-gray-700 disabled:opacity-40"
                >
                  {t("certificates.next", { defaultValue: "Next" })}
                </button>
              </div>
            </div>
          )}
        </div>
          </>
        )}

        {tab === "branding" && <CertificateBranding />}
      </div>

      {showIssueModal && (
        <IssueCertificateModal
          onClose={() => setShowIssueModal(false)}
          onConfirm={handleIssue}
          onBatchComplete={handleBatchFinished}
        />
      )}
      {revokeTarget && (
        <RevokeCertificateModal
          cert={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onConfirm={handleRevokeConfirm}
        />
      )}
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────

function StatCard({ icon: Icon, iconColor, bg, label, value }) {
  return (
    <div className="rounded-[16px] border border-gray-200 bg-white p-5 flex items-center gap-4">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${bg} ${iconColor}`}>
        <Icon size={20} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
        <div className="text-2xl font-bold text-gray-900 mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function CertRow({ cert, onRevoke, onRequeue }) {
  const { t } = useTranslation();
  const isRevoked = cert.status === "REVOKED";
  const renderPending = cert.status === "ISSUED" && !cert.pdfUrl;

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50 transition">
      <td className="px-5 py-3">
        <div className="text-sm font-medium text-gray-900">{cert.studentDisplayName}</div>
        <div className="text-xs text-gray-500 font-mono mt-0.5">{cert.verificationCode}</div>
      </td>
      <td className="px-5 py-3">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
          {TYPE_LABEL[cert.type] || cert.type}
        </span>
      </td>
      <td className="px-5 py-3 max-w-[260px]">
        <div className="text-sm text-gray-900 truncate" title={cert.title}>
          {cert.title}
        </div>
      </td>
      <td className="px-5 py-3 text-sm text-gray-600 whitespace-nowrap">
        {formatDate(cert.createdAt)}
      </td>
      <td className="px-5 py-3">
        {isRevoked ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold">
            <XCircle size={11} /> {t("certificates.statusRevoked", { defaultValue: "Revoked" })}
          </span>
        ) : renderPending ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
            <Loader2 size={11} className="animate-spin" /> {t("certificates.statusRendering", { defaultValue: "Rendering" })}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
            <BadgeCheck size={11} /> {t("certificates.statusIssued", { defaultValue: "Issued" })}
          </span>
        )}
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-1.5">
          {cert.pdfUrl && (
            <a
              href={cert.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[8px] border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              title={t("certificates.actions.viewPdf", { defaultValue: "View PDF" })}
            >
              <FileText size={12} /> PDF
            </a>
          )}
          {renderPending && (
            <button
              type="button"
              onClick={onRequeue}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[8px] border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              title={t("certificates.actions.requeue", { defaultValue: "Re-render" })}
            >
              <RefreshCcw size={12} /> {t("certificates.actions.retry", { defaultValue: "Retry" })}
            </button>
          )}
          <a
            href={buildVerifyUrl(cert.verificationCode)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[8px] border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            title={t("certificates.actions.verify", { defaultValue: "Open verify page" })}
          >
            <ExternalLink size={12} />
          </a>
          {!isRevoked && (
            <button
              type="button"
              onClick={onRevoke}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[8px] border border-red-200 text-xs font-semibold text-red-700 hover:bg-red-50"
            >
              {t("certificates.actions.revoke", { defaultValue: "Revoke" })}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function EmptyState({ onIssue }) {
  const { t } = useTranslation();
  return (
    <div className="p-10 flex flex-col items-center text-center gap-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
        <Award size={22} />
      </div>
      <div>
        <h4 className="text-base font-bold text-gray-900">
          {t("certificates.empty.title", { defaultValue: "No certificates issued yet" })}
        </h4>
        <p className="text-sm text-gray-500 mt-1 max-w-md">
          {t("certificates.empty.subtitle", {
            defaultValue:
              "Issue your first certificate to a student. They'll get an email and the cert will land in their Hypebook Vault.",
          })}
        </p>
      </div>
      <button
        type="button"
        onClick={onIssue}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] bg-primary text-white text-sm font-semibold"
      >
        <Plus size={14} />
        {t("certificates.empty.cta", { defaultValue: "Issue first certificate" })}
      </button>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

/** Build the public verify URL on the Student app. Canonical env var is
 *  VITE_STUDENT_URL (shared app-wide); VITE_STUDENT_APP_URL is honored as a
 *  legacy fallback from before the names were consolidated. School admins
 *  click this to confirm the public-facing surface looks right. */
function buildVerifyUrl(code) {
  const base = import.meta.env.VITE_STUDENT_URL || import.meta.env.VITE_STUDENT_APP_URL || "https://app.hypebook.app";
  return `${base.replace(/\/$/, "")}/verify/cert/${encodeURIComponent(code)}`;
}
